/**
 * Resolving stored EAV attribute keys to the canonical vocabulary (MAR-176).
 *
 * `attribute_keys.key` is FREE TEXT, org-scoped, and written by two very different producers: the
 * identity pipeline, which uses the closed English set in `shared/vocabulary/crossService.ts`, and
 * the general entity extractor, which writes whatever the document called the field. The dev corpus
 * therefore holds both spellings of the same fact — `address` and `direccion`, `municipality` and
 * `municipio`, `nationality` and `nacionalidad`, `occupation` and `profesion` — on entities in the
 * same organization.
 *
 * A run-form input that wants "the party's municipality" cannot ask for one of those and be right.
 * So the keys are resolved to a canonical name AT READ TIME, here. Deliberately **not** a data
 * rewrite: the stored key is what a document actually called the field, which is worth keeping, and
 * a migration would have to guess for every key nobody has seen yet. Reading through an alias map
 * costs nothing and stays correct when the next extraction invents a new spelling.
 *
 * **The canonical list is not declared here.** It IS the party-attribute vocabulary — the set of
 * facts a party can carry is the same set a run-form input can bind to, and they must not be two
 * lists. They briefly were: this file shipped its own 12-member list against the extractor's 9,
 * which made `company` requestable by the run form but unnameable by the extractor, and `phone` /
 * `email` requestable by nothing at all. Adding a canonical key now means adding it to
 * `crossService.ts`, where it gets a label and reaches both services.
 *
 * Adding an ALIAS, by contrast, belongs here: an alias is a spelling the corpus happens to hold, not
 * a new fact.
 */
import {
  PARTY_ATTRIBUTE_KEYS,
  ENTITY_ATTRIBUTE_KEYS,
  type EntityAttributeKey,
} from '../vocabulary/crossService'

/**
 * The facts a person party-block input may bind to via `entitySource.field: 'attribute:<key>'` —
 * exactly the facts a person party can carry.
 */
export const CANONICAL_ATTRIBUTE_KEYS = PARTY_ATTRIBUTE_KEYS
export type CanonicalAttributeKey = EntityAttributeKey

/** Fold accents, case and separators so `Municipio`, `municipio` and `MUNICIPIO ` are one key. */
function fold(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Folded alias → canonical key. Every canonical key maps to itself implicitly.
 *
 * The Spanish entries are not hypothetical: they were read off the dev corpus, where the extractor
 * had written them alongside the English ones on entities in the same organization.
 */
const ALIASES: Record<string, CanonicalAttributeKey> = {
  // person given names and surnames (never split from composed `name`; only resolved when explicitly stored)
  nombres: 'given_names', primer_nombre: 'given_names', first_name: 'given_names', given_name: 'given_names',
  apellidos: 'surnames', apellido: 'surnames', last_name: 'surnames', surname: 'surnames',
  // address — the STREET address or property location, where the entity lives, operates or sits.
  direccion: 'address', residencia: 'address',
  direccion_residencia: 'address', address_line: 'address', direccion_completa: 'address',
  ubicacion: 'address', ubicacion_inmueble: 'address',
  /**
   * municipality / city — one fact, and NOT the same as `address`.
   *
   * `domicilio` maps here, not to `address`. In Salvadoran drafting the comparecencia states a
   * party's *domicilio* (the municipality they are domiciled in) separately from their *dirección*
   * (the street address), and templates carry both: `Contrato Individual de Trabajo` has
   * `domicilio_trabajador` AND `direccion_residencia_trabajador` for the same person. Folding
   * `domicilio → address` made them compete for one fact, so filling a party block would have put
   * the street address into the domicilio blank. Only visible once party blocks force both fields
   * into view together (MAR-176).
   */
  domicilio: 'municipality',
  municipio: 'municipality', ciudad: 'municipality', city: 'municipality',
  localidad: 'municipality', poblacion: 'municipality',
  // department / state / province
  departamento: 'department', provincia: 'department', estado: 'department',
  state: 'department', province: 'department',
  // nationality
  nacionalidad: 'nationality',
  // occupation
  profesion: 'occupation', oficio: 'occupation', profesion_u_oficio: 'occupation',
  ocupacion: 'occupation', occupation_or_trade: 'occupation',
  // marital status
  estado_civil: 'marital_status', estado_familiar: 'marital_status',
  // dates and places
  fecha_de_nacimiento: 'birth_date', fecha_nacimiento: 'birth_date',
  lugar_de_nacimiento: 'place_of_birth', lugar_nacimiento: 'place_of_birth',
  lugar_expedicion: 'id_issue_place', lugar_de_expedicion: 'id_issue_place',
  lugar_expedicion_dui: 'id_issue_place', lugar_de_expedicion_dui: 'id_issue_place',
  lugar_expedicion_del_dui: 'id_issue_place', lugar_de_expedicion_del_dui: 'id_issue_place',
  lugar_expedicion_documento: 'id_issue_place', lugar_de_expedicion_documento: 'id_issue_place',
  lugar_emision: 'id_issue_place', lugar_de_emision: 'id_issue_place',
  lugar_emision_dui: 'id_issue_place', lugar_de_emision_dui: 'id_issue_place',
  fecha_expedicion: 'id_issue_date', fecha_de_expedicion: 'id_issue_date',
  fecha_expedicion_dui: 'id_issue_date', fecha_de_expedicion_dui: 'id_issue_date',
  fecha_expedicion_del_dui: 'id_issue_date', fecha_de_expedicion_del_dui: 'id_issue_date',
  fecha_expedicion_documento: 'id_issue_date', fecha_de_expedicion_documento: 'id_issue_date',
  fecha_emision: 'id_issue_date', fecha_de_emision: 'id_issue_date',
  fecha_emision_dui: 'id_issue_date', fecha_de_emision_dui: 'id_issue_date',
  // sex
  sexo: 'sex', genero: 'sex', gender: 'sex',
  // employer / company the party belongs to
  empresa: 'company', empleador: 'company', patrono: 'company', employer: 'company',
  razon_social: 'company', compania: 'company',
  // organization & secondary identifier facts
  nit: 'tax_id', identificacion_tributaria: 'tax_id', ruc: 'tax_id', rfc: 'tax_id', tax_id: 'tax_id',
  matricula_de_comercio: 'registration_number', matricula_comercio: 'registration_number',
  registro_mercantil: 'registration_number', business_registration: 'registration_number',
  passport: 'passport', pasaporte: 'passport',
  foreigner_id: 'foreigner_id', carne_de_residente: 'foreigner_id', carnet_de_residente: 'foreigner_id', resident_card: 'foreigner_id',
  national_id: 'national_id', dui: 'national_id', documento_unico_de_identidad: 'national_id',
  forma_juridica: 'legal_form', tipo_societario: 'legal_form',
  representante_legal: 'representative', representante: 'representative', apoderado: 'representative',
  giro: 'economic_activity', actividad_economica: 'economic_activity', giro_comercial: 'economic_activity',
  pais: 'country',
  // property facts (SV CNR / catastro)
  matricula: 'registry_number', matricula_cnr: 'registry_number', inscripcion_cnr: 'registry_number', numero_de_matricula: 'registry_number',
  numero_catastral: 'cadastral_number', catastro: 'cadastral_number', clave_catastral: 'cadastral_number',
  superficie: 'area', extension_superficial: 'area', cabida: 'area',
  // vehicle facts (SV SERTRACEN)
  placa: 'plate', numero_de_placa: 'plate', placas: 'plate',
  chasis: 'vin', numero_de_chasis: 'vin', numero_chasis: 'vin', numero_chasis_vin: 'vin', serie: 'vin',
  numero_de_motor: 'engine_number', numero_motor: 'engine_number', motor: 'engine_number',
  marca: 'make',
  modelo: 'model',
  ano: 'year', anio: 'year', ano_de_fabricacion: 'year',
}

/**
 * Resolve a stored attribute key to its canonical name, or null when it is not one of the facts a
 * workflow input can bind to.
 *
 * Null rather than a passthrough on purpose: an unrecognised key is a real attribute the party
 * carries, but binding an input to it would mean the input silently fills from whatever key happened
 * to fold that way. Unknown keys stay readable in the Briefs UI; they just are not autofill sources.
 */
export function canonicalAttributeKey(raw: string | null | undefined): CanonicalAttributeKey | null {
  if (!raw) return null
  const key = fold(raw)
  if (!key) return null
  if ((ENTITY_ATTRIBUTE_KEYS as readonly string[]).includes(key)) return key as CanonicalAttributeKey
  return ALIASES[key] ?? null
}

/**
 * How an attribute's VALUE should be rendered and edited — `attribute_keys.data_type`.
 *
 * Orthogonal to the key: `company` is text, `birth_date` is a date, and a key the corpus invented
 * carries whatever the writer said. This is a presentation hint, never a validation rule — values
 * stay free text (see `PARTY_ATTRIBUTE_KEYS`), because a stored value that fails its own type would
 * be unreadable rather than merely ugly.
 *
 * Declared once because it was declared three times: two server-side validators
 * (`server/services/entities/index.ts`, `server/services/briefs/index.ts`) and the editor's dropdown
 * (`app/pages/app/briefs/parties/[id].vue`). The picker offers what the servers accept, so a drift
 * there is a dropdown option the server silently rewrites to `text` — a change the user watches
 * themselves make and that then does not stick.
 */
export const ATTRIBUTE_DATA_TYPES = ['text', 'email', 'phone', 'url', 'date', 'number'] as const
export type AttributeDataType = (typeof ATTRIBUTE_DATA_TYPES)[number]

/**
 * The type a BUILT-IN key's value naturally has — what the party editor presets a row to when the key
 * is picked (MAR-1472). Only keys whose type is something other than `text` are listed; anything
 * absent is `text`. A presentation hint like the rest: it picks the input, it never checks the value.
 *
 * `area` is deliberately absent — "120 m²" is how an area is written, and a numeric keypad would only
 * get in the way of the unit.
 */
export const CANONICAL_ATTRIBUTE_DATA_TYPES: Partial<Record<EntityAttributeKey, AttributeDataType>> = {
  birth_date: 'date',
  id_issue_date: 'date',
  year: 'number',
}

/** Coerce a client-supplied `data_type` to a member, defaulting to `text`. */
export function toAttributeDataType(raw: unknown): AttributeDataType {
  return typeof raw === 'string' && (ATTRIBUTE_DATA_TYPES as readonly string[]).includes(raw)
    ? raw as AttributeDataType
    : 'text'
}

/** Type guard for an authored `entitySource.field: 'attribute:<key>'` value. */
export function isCanonicalAttributeKey(v: unknown): v is CanonicalAttributeKey {
  return typeof v === 'string' && (ENTITY_ATTRIBUTE_KEYS as readonly string[]).includes(v)
}
